import React, { useState } from "react";
import styled from "@emotion/styled";
import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { useRangeSelectControl } from "@hooks";
import { Tab, Tabs } from "@components";
import { InvestmentsTable } from "@features/investments/investment/components/InvestmentTable";
import { useListAndSearchAllInvestments } from "@bn/api/apiv1";
import { InvestmentActions } from "@features/depots/components/DepotsDashboard/InvestmentActions";
import { MandatorTypography } from "@components/MandatorTypography";
import { InvestmentsChartDataStatistic } from "@api/v1/hooks/performance/useInvestmentsChartData";
import { keepPreviousData } from "@tanstack/react-query";
import { AssetTypeBreakdown } from "./AssetTypeBreakdown";
import { InvestmentsTableSummary } from "./InvestmentsTableSummary/InvestmentsTableSummary";
import { BookingsTableContainer } from "./BookingsTableContainer";

const BookingsTableWrapper = styled.div`
  margin-top: ${({ theme }) => theme.spacing(6)};
`;

interface DepotsDashboardContentProps {
  accountIds: number[];
  statistic: InvestmentsChartDataStatistic;
  /** Set on the single-depot page so investment rows link under that depot. */
  depotId?: number;
}

export const DepotsDashboardContent = ({
  accountIds,
  statistic,
  depotId,
}: DepotsDashboardContentProps) => {
  const { t } = useTranslation("");
  const [searchParams, setSearchParams] = useSearchParams();

  const [showHistoric, setShowHistoric] = useState(false);

  const handleTabChange = (key: string) => {
    setSearchParams({ tab: key });
  };

  const onShowHistoricChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setShowHistoric(event.target.checked);

  const {
    data: investmentsResult,
    isLoading: isInvestmentsLoading,
    isPending: isInvestmentsPending,
    isFetching: isInvestmentsFetching,
  } = useListAndSearchAllInvestments(
    {
      accountId: accountIds,
      includeHistoric: showHistoric,
    },
    {
      placeholderData: keepPreviousData,
    }
  );

  const rangeData = useRangeSelectControl({
    firstDate: new Date(),
  });

  const investments = investmentsResult?.investments ?? [];

  const defaultTabKey = searchParams.get("tab");

  return (
    <>
      <AssetTypeBreakdown accountIds={accountIds} />

      <MandatorTypography variant="h5">
        {t("investmentsPage.overview")}
      </MandatorTypography>

      <Tabs
        onChange={handleTabChange}
        defaultKey={defaultTabKey}
        buttonElement={
          <InvestmentActions
            onShowHistoricChange={onShowHistoricChange}
            accountIds={accountIds}
            minDate={rangeData?.range?.startDate?.toISOString()}
            maxDate={rangeData?.range?.endDate.toISOString()}
            showHistoric={showHistoric}
          />
        }
      >
        <Tab
          label={t("components.depotsStatistic.tabs.investments")}
          key="investments"
        >
          <InvestmentActions
            onShowHistoricChange={onShowHistoricChange}
            accountIds={accountIds}
            minDate={rangeData?.range?.startDate?.toISOString()}
            maxDate={rangeData?.range?.endDate.toISOString()}
            showHistoric={showHistoric}
            activeTab="investments"
            inTabsBar={false}
          />
          <InvestmentsTableSummary statistic={statistic} />
          <InvestmentsTable
            className="investments-table"
            depotId={depotId}
            investments={investments}
            isLoading={isInvestmentsLoading}
            isFetching={isInvestmentsFetching}
            isPending={isInvestmentsPending}
          />
        </Tab>
        <Tab
          label={t("components.depotsStatistic.tabs.bookings")}
          key="bookings"
        >
          <InvestmentActions
            onShowHistoricChange={onShowHistoricChange}
            accountIds={accountIds}
            minDate={rangeData?.range?.startDate?.toISOString()}
            maxDate={rangeData?.range?.endDate.toISOString()}
            showHistoric={showHistoric}
            activeTab="bookings"
            inTabsBar={false}
          />
          <BookingsTableWrapper>
            <BookingsTableContainer
              rangeData={rangeData}
              summaryValue={statistic.portfolioValue}
              accountIds={accountIds ?? []}
              depotId={depotId}
            />
          </BookingsTableWrapper>
        </Tab>
      </Tabs>
    </>
  );
};
